import { useEffect } from 'react';
import { useRouter } from 'next/router';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import Image from '@base/Image';
import useWindowSize from '@hooks/useWindowSize';
import Button from '@base/Button/Button';
import { css } from '@emotion/react';
import { segment } from '@actions/analytics';
import { Banner_Banner_Viewed } from '@constants/segment';
import { BANNER_CLICKED } from '@cont/EducatorProfile/constants';
import { getDateMonthString } from '@utils/dates';
import DangerousWithSanitization from '@base/DangerousWithSanitization';
import {
  APP_PLAY_STORE_LINK,
  COMPETE_IMAGES,
  COMPETE_LANDING_PAGE,
  COMPETE_STATES
} from './constants';
import useCompeteConfig from './useCompeteConfig';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  background: var(--color-i-violet-22);
  text-align: center;
  padding: ${Spacings.SPACING_18B} 0 98px;
  padding-bottom: ${Spacings.SPACING_9B};

  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_14B} ${Spacings.SPACING_4B} 0;
  }
  z-index: 1;
`;

const LeftImage = styled.div`
  position: absolute;
  top: 70px;

  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const RightImage = styled.div`
  position: absolute;
  top: 60px;
  right: 0;

  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const Title = styled.div`
  font-weight: ${Fonts.BOLD};
  font-size: ${Fonts.H2};
  line-height: ${Spacings.SPACING_10B};
  color: var(--color-i-white);
  margin: 0 auto ${Spacings.SPACING_10B};
  max-width: 568px;

  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H3};
    line-height: ${Spacings.SPACING_8B};
    margin-bottom: ${Spacings.SPACING_8B};
  }
`;

const SubText = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--color-i-white);
  font-size: ${Fonts.H5};
  line-height: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_8B};

  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.H6};
    line-height: 18px;
    margin-bottom: ${Spacings.SPACING_4B};
  }

  img {
    margin-right: ${Spacings.SPACING_2B};
  }

  .right-line {
    transform: rotate(180deg);
  }
`;

const LineWrapper = styled.div`
  position: relative;
  margin: 0 ${Spacings.SPACING_3B};
`;

const StyledBtn = styled(Button)`
  background-color: var(--color-i-white);
  color: var(--color-i-icon-dark-tertiary);
  width: 160px;
  height: ${Spacings.SPACING_12B};
  min-height: ${Spacings.SPACING_12B};
  font-size: ${Fonts.H6};
  line-height: ${Spacings.SPACING_5B};

  &:hover {
    box-shadow: 0px 6px 20px var(--color-i-black-8);
  }

  &:active {
    box-shadow: 0px 6px 20px var(--color-i-black-8);
    background: var(--color-i-gray-29);
  }

  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const StyledBtn2 = styled(StyledBtn)`
  background-color: transparent;
  color: var(--color-i-white);
  border: 1px solid white;
  margin-left: ${Spacings.SPACING_4B};
  :hover {
    background-color: var(--color-i-white);
    color: var(--color-i-icon-dark-tertiary);
  }

  ${mediaQuery.uptoMobile} {
    margin-left: 0;
  }
`;

const LaunchWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${Fonts.BOLD};
  font-size: ${Spacings.SPACING_4B};
  line-height: ${Spacings.SPACING_6B};
  color: var(--color-i-blue-34);
  > p {
    margin: 0 ${Spacings.SPACING_1B};
  }
  margin-top: ${Spacings.SPACING_6B};

  ${mediaQuery.uptoMobile} {
    font-size: 12px;
    line-height: ${Spacings.SPACING_4B};
  }
`;

const TNCText = styled.div`
  margin-top: ${Spacings.SPACING_10B};
  font-size: 10px;
  line-height: ${Spacings.SPACING_4B};
  text-align: center;
  color: var(--color-i-blue-33);
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_4B};
    padding-bottom: ${Spacings.SPACING_5B};
  }
`;

const Gap = styled.div`
  margin-top: ${Spacings.SPACING_16B};
  padding-bottom: ${Spacings.SPACING_5B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_8B};
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  ${mediaQuery.uptoMobile} {
    flex-direction: column;
  }
`;

const BackgroundGlow = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  left: 5px;
`;

const LogoWrapper = styled.div`
  ${(props) =>
    props.alignImage
      ? css`
          position: relative;
          top: 16px;
          left: 5px;
        `
      : null}
`;

const sendEventOnClick = (ctaLabel, goalUID, isLive) => {
  segment.track(BANNER_CLICKED, {
    cta_button: ctaLabel,
    banner_title: isLive ? 'Live' : 'Post Launch',
    goal_uid: goalUID,
    banner_type: 'Compete Awareness'
  });
};

const CompeteLaunchBanner = () => {
  const { isMobile } = useWindowSize();
  const {
    isCompete,
    state,
    launchDate,
    title,
    showWinnerPrize,
    winnerPrizeTitle
  } = useCompeteConfig();
  const {
    query: { goalUID }
  } = useRouter();

  const isLive = state === COMPETE_STATES.LIVE;

  useEffect(() => {
    if (isCompete) {
      segment.track(Banner_Banner_Viewed, {
        banner_title: isLive ? 'Live' : 'Post Launch',
        banner_type: 'Compete Awareness',
        goal_uid: goalUID
      });
    }
  }, [goalUID, isLive, isCompete]);

  if (!isCompete || state === COMPETE_STATES.POST_LAUNCH) {
    return null;
  }

  const launchTitle = isLive
    ? 'NOW LIVE'
    : `LAUNCHING ${getDateMonthString(launchDate ?? '').toUpperCase()}`;
  const ctaLabelOne = isLive ? 'Get the app' : 'Check it out';

  const handleCTAOne = () => {
    sendEventOnClick(ctaLabelOne, goalUID, isLive);
    if (isLive) {
      return window.open(APP_PLAY_STORE_LINK);
    }
    window.open(COMPETE_LANDING_PAGE);
  };

  const handleCTATwo = () => {
    sendEventOnClick('Check it out', goalUID, isLive);
    window.open(COMPETE_LANDING_PAGE);
  };

  return (
    <Wrapper>
      <BackgroundGlow>
        <Image
          src={
            isMobile ? '/compete/bg-glow-mobile.svg' : '/compete/bg-glow.svg'
          }
          layout="fill"
          alt="compete"
        />
      </BackgroundGlow>
      <LeftImage>
        <Image
          src={COMPETE_IMAGES.BANNER_LEFT}
          width={300}
          height={532}
          alt="clip-left"
        />
      </LeftImage>
      <RightImage>
        <Image
          src={COMPETE_IMAGES.BANNER_RIGHT}
          width={300}
          height={532}
          alt="clip-right"
        />
      </RightImage>
      <LaunchWrapper>
        <Image src="/compete/star.svg" width={12} height={12} alt="icon" />
        <p>{launchTitle}</p>
        <Image src="/compete/star.svg" width={12} height={12} alt="icon" />
      </LaunchWrapper>

      <LogoWrapper alignImage={isLive && isMobile}>
        <Image
          src={COMPETE_IMAGES.LOGO}
          width={isMobile ? 252 : 350}
          height={isMobile ? 140 : 180}
          objectFit="contain"
          alt="icon"
        />
      </LogoWrapper>
      <DangerousWithSanitization as={Title} html={title} />
      {showWinnerPrize && (
        <SubText>
          {!isMobile && (
            <LineWrapper>
              <Image
                src="/compete/styled-line.svg"
                width={80}
                height={12}
                alt="icon"
              />
            </LineWrapper>
          )}
          {winnerPrizeTitle}
          {!isMobile && (
            <LineWrapper className="right-line">
              <Image
                src="/compete/styled-line.svg"
                width={80}
                height={12}
                alt="icon"
              />
            </LineWrapper>
          )}
        </SubText>
      )}
      <ButtonWrapper>
        <StyledBtn label={ctaLabelOne} onClick={handleCTAOne} />
        {isLive && <StyledBtn2 label="Check it out" onClick={handleCTATwo} />}
      </ButtonWrapper>
      {showWinnerPrize ? (
        <TNCText>*Terms and Conditions apply</TNCText>
      ) : (
        <Gap />
      )}
    </Wrapper>
  );
};

export default CompeteLaunchBanner;
